import React from 'react';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Send from '@atlaskit/icon/core/send';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useElementBreakpoints, Breakpoint } from '../../hooks/useElementBreakpoints';
import type { Suggestion } from '../Suggestions/types';

export type Props = {
	inputValue: string;
	onFormSubmit: (inputValue: string) => void;
	setSuggestion: (suggestion: Suggestion) => void;
	showButton: boolean;
	suggestions: Suggestion[];
};

const lozengeContainerStyleBig = cssMap({
	default: {
		display: 'flex',
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		gap: token('space.100'),
		alignItems: 'center',
	},
});

const lozengeContainerStyle = cssMap({
	default: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
	},
});

export const CommandPaletteLozenge = ({
	onFormSubmit,
	inputValue,
	suggestions,
	setSuggestion,
	showButton,
}: Props): React.JSX.Element => {
	const highlightedSuggestion = suggestions.find((suggestion) => suggestion.value === 'Brainstorm');

	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints();

	/**
	 * This is a quick spike intermediate solution to determine how many suggestions to display
	 */
	const howManySuggestionsToDisplay = (s: Suggestion[]): number => {
		if (!s.length) {
			return 0;
		}

		const isSmall = breakpoint <= Breakpoint.Small;
		const first = s[0];

		if (first.value === 'Brainstorm') {
			return 1;
		}

		const isTopLevel = !first.nestingConfig || first.label === first.nestingConfig.parentTitle;

		if (isTopLevel) {
			return isSmall ? 1 : 3;
		}

		return isSmall ? 1 : s.length;
	};

	return (
		<Inline
			ref={setBreakpointsElement}
			spread="space-between"
			xcss={lozengeContainerStyleBig.default}
		>
			<Inline xcss={lozengeContainerStyle.default}>
				{!inputValue &&
					// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
					suggestions
						.filter(
							(suggestion) => suggestion.value !== 'tell-ai' && suggestion.value !== 'show-more',
						)
						.slice(0, howManySuggestionsToDisplay(suggestions))
						.map((suggestion) => (
							<Button
								spacing="compact"
								key={`${suggestion.value}-button`}
								// eslint-disable-next-line @atlassian/perf-linting/detect-unnecessary-rerenders, @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								onClick={() => suggestion.onSelect()}
								// eslint-disable-next-line @atlassian/perf-linting/detect-unnecessary-rerenders, @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								onMouseEnter={() => suggestion.onSuggestionFocused()}
								// eslint-disable-next-line @atlassian/perf-linting/detect-unnecessary-rerenders, @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								onMouseLeave={() =>
									setSuggestion(highlightedSuggestion ? highlightedSuggestion : suggestion)
								}
							>
								{suggestion.label}
							</Button>
						))}
			</Inline>
			{showButton && (
				<IconButton
					icon={Send}
					spacing="compact"
					label="send-icon-command-palette-lozenge"
					isDisabled={inputValue === ''}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					onClick={() => {
						onFormSubmit(inputValue);
					}}
				/>
			)}
		</Inline>
	);
};
